<template>
  <section
    v-if="visible"
    class="license-banner"
    :class="`license-banner--${license.effective_status}`"
    role="region"
    :aria-label="$t('license.banner.region_label')"
    aria-live="polite"
    data-test="license-status-banner"
  >
    <v-icon class="license-banner__icon" aria-hidden="true">{{ icon }}</v-icon>
    <div class="license-banner__copy">
      <strong data-test="license-state-label">{{ $t(`license.banner.${license.effective_status}.title`) }}</strong>
      <span>{{ $t(`license.banner.${license.effective_status}.body`) }}</span>
      <time v-if="deadline" :datetime="deadline" data-test="license-deadline">
        {{ $t('license.deadline', { value: formattedDeadline }) }}
      </time>
      <span v-if="license.offline" data-test="license-sync-note">
        {{ $t('license.sync_stale', { value: formattedLastSync }) }}
      </span>
    </div>
    <v-btn
      v-if="showHistoryExportAction"
      flat
      small
      class="license-banner__action"
      data-test="license-history-export-link"
      @click="$router.push('/history-export')"
    >
      {{ $t('license.history_export.open') }}
    </v-btn>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  computed: {
    ...mapGetters({ license: 'getLicense' }),
    visible() {
      return ['grace', 'degraded', 'suspended'].includes(this.license.effective_status)
    },
    icon() {
      return this.license.effective_status === 'suspended' ? 'lock' : 'warning'
    },
    deadline() {
      if (!this.license.deadlines) return null
      if (this.license.effective_status === 'grace') return this.license.deadlines.grace_until
      if (this.license.effective_status === 'degraded') return this.license.deadlines.degraded_until
      return null
    },
    formattedDeadline() {
      if (!this.deadline) return ''
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        timeZone: 'Asia/Bangkok',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZoneName: 'short',
      }).format(new Date(this.deadline))
    },
    formattedLastSync() {
      if (!this.license.last_synced_at) return this.$t('license.sync_unknown')
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        timeZone: 'Asia/Bangkok',
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
      }).format(new Date(this.license.last_synced_at))
    },
    isHistoryExportPage() {
      return this.$route && (this.$route.name === 'HistoricalExport' ||
        (this.$route.matched || []).some(record => record.name === 'HistoricalExport'))
    },
    showHistoryExportAction() {
      return Boolean(this.license.capabilities['history.export']) && !this.isHistoryExportPage
    },
  },
}
</script>

<style lang="scss" scoped>
.license-banner {
  min-height: 52px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px 8px 12px;
  border-left: 4px solid #f9a825;
  background: #fff8e1;
  color: #4e342e;
  position: sticky;
  top: 0;
  z-index: 8;
  animation: license-banner-enter 160ms ease-out both;
  transition: color 200ms, background-color 200ms, border-color 200ms;

  &--degraded { border-left-color: #ef6c00; background: #fff3e0; }
  &--suspended { border-left-color: #c62828; background: #ffebee; color: #7f0000; }
  &__icon { color: inherit; }
  &__copy { display: flex; flex: 1; flex-wrap: wrap; gap: 4px 12px; align-items: baseline; }
  > .license-banner__action { min-width: 44px; min-height: 44px; padding-right: 12px; padding-left: 12px; }
  time { font-variant-numeric: tabular-nums; }
}

@keyframes license-banner-enter {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 600px) {
  .license-banner { align-items: flex-start; }
  .license-banner__copy { display: grid; gap: 2px; }
}

@media (prefers-reduced-motion: reduce) {
  .license-banner,
  .license-banner * { animation: none !important; transition: none !important; }
}
</style>
